import { useRef, type ReactNode } from "react";

import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { cn } from "@/lib/utils";
import type { SidebarItem } from "@/types/navigation";
import { AppLayout } from "./AppLayout";
import { PageBreadcrumb, type BreadcrumbCrumb } from "./PageBreadcrumb";
import { PageHeader, type PageHeaderControls, type PageTitleAction } from "./PageHeader";
import { PresentationLayout } from "./PresentationLayout";
import { PresentationHeaderContext, usePageHeaderHost } from "./PresentationHeaderSlots";
import { SectionSidePanel } from "./SectionSidePanel";
import { useFitToScreen } from "./useFitToScreen";
import { usePinnedHeader } from "./usePinnedHeader";
import { usePresentationMode } from "./usePresentationMode";

/**
 * How the page content is laid out under the header:
 * - `page`: contained width — lists, tables, settings (default);
 * - `narrow`: a reading width for forms;
 * - `dashboard`: full width — monitoring uses the whole screen; widgets keep
 *   their own reading widths — with the dashboard's roomier spacing;
 * - `canvas`: full width, unpadded, scrolling inside (the dashboard editor).
 */
export type SectionLayoutVariant = "page" | "narrow" | "dashboard" | "canvas";

const WIDTH: Record<SectionLayoutVariant, string> = {
  page: "mx-auto w-full max-w-screen-2xl",
  narrow: "mx-auto w-full max-w-5xl",
  dashboard: "w-full",
  canvas: "w-full",
};

const SPACING: Record<SectionLayoutVariant, { header: string; content: string }> = {
  page: { header: "pb-(--density-page-top)", content: "density-page-padding" },
  narrow: { header: "pb-(--density-page-top)", content: "density-page-padding" },
  dashboard: {
    header: "pb-(--density-dashboard-page-top)",
    content: "density-dashboard-page-padding",
  },
  canvas: { header: "", content: "" },
};

interface SectionLayoutProps extends PageHeaderControls {
  sectionTitle: string;
  sidebarItems: SidebarItem[];
  /** Also used for the document title and accessible page name. */
  title: string;
  titleLoading?: boolean;
  titleAction?: PageTitleAction;
  /** One line under the title; its space is reserved even when empty. */
  description?: ReactNode;
  /** Browser tab title, when it should differ from `title`. */
  documentTitle?: string;
  breadcrumbTrail?: readonly BreadcrumbCrumb[];
  hideHeader?: boolean;
  variant?: SectionLayoutVariant;
  /** Offers the full-screen presentation (More menu and Shift+F). */
  presentation?: boolean;
  /** Overrides the cyan selected-tenant indicator at the top edge. */
  tenantScopeRail?: boolean;
  children: ReactNode;
}

/** Shell of every section page: navigation, page header, then the content. */
export function SectionLayout({
  sectionTitle,
  sidebarItems,
  title,
  titleLoading = false,
  titleAction,
  description,
  documentTitle,
  breadcrumbTrail,
  hideHeader = false,
  variant = "page",
  presentation = false,
  tenantScopeRail,
  children,
  ...controls
}: SectionLayoutProps) {
  useDocumentTitle(documentTitle ?? title, sectionTitle);
  const presentationMode = usePresentationMode(presentation);
  const canvas = variant === "canvas";
  const headerRef = useRef<HTMLElement>(null);
  usePinnedHeader(headerRef, canvas);
  // A dashboard may name its page in this header, beside its own title, as full screen does.
  const pageHost = usePageHeaderHost("page");
  // A dashboard that fits the window fills it, as in full screen; a longer one scrolls.
  const contentRef = useRef<HTMLDivElement>(null);
  useFitToScreen(
    contentRef,
    contentRef,
    "page",
    variant === "dashboard" && !presentationMode.active,
  );

  if (presentationMode.active) {
    return (
      <PresentationLayout
        title={title}
        titleLoading={titleLoading}
        description={description}
        titleVisual={controls.titleVisual}
        status={controls.status}
        statusRef={controls.statusRef}
        contextRef={controls.contextRef}
        tenantScopeRail={tenantScopeRail}
        filters={controls.filters}
        previewFilters={controls.previewFilters}
        viewActions={controls.viewActions}
        onExit={presentationMode.exit}
      >
        {children}
      </PresentationLayout>
    );
  }

  return (
    <AppLayout
      topbarBreadcrumb={
        <PageBreadcrumb
          sectionTitle={sectionTitle}
          sidebarItems={sidebarItems}
          title={title}
          titleLoading={titleLoading}
          breadcrumbTrail={breadcrumbTrail}
        />
      }
      viewportLocked={canvas}
      tenantScopeRail={tenantScopeRail}
    >
      <div
        className={cn(
          "relative flex flex-1 flex-col bg-background",
          canvas ? "min-h-0 overflow-hidden" : "min-h-[calc(100dvh-3.5rem)]",
        )}
      >
        <main
          id="main-content"
          tabIndex={-1}
          aria-label={title}
          className={cn("flex min-h-0 flex-1 flex-col", canvas && "overflow-hidden")}
        >
          {!hideHeader && (
            <PageHeader
              ref={headerRef}
              pageSlots={pageHost.paged ? pageHost.refs : undefined}
              pageCommands={pageHost.commands}
              pageControlsSlot={pageHost.refs.controls}
              title={title}
              titleLoading={titleLoading}
              titleAction={titleAction}
              description={description}
              widthClassName={WIDTH[variant]}
              onPresent={presentation ? presentationMode.enter : undefined}
              className={cn("pt-4", SPACING[variant].header, canvas && "top-0")}
              {...controls}
            />
          )}
          <div
            ref={contentRef}
            data-page-content
            className={cn(
              "@container/pagecontent pt-0",
              canvas ? "flex min-h-0 flex-1 flex-col" : "min-h-0 shrink-0",
              SPACING[variant].content,
              WIDTH[variant],
            )}
          >
            {/* Without a header there is nowhere to name the page: it keeps its own band. */}
            <PresentationHeaderContext value={hideHeader ? null : pageHost.context}>
              {children}
            </PresentationHeaderContext>
          </div>
        </main>
        <SectionSidePanel />
      </div>
    </AppLayout>
  );
}
